import React, { Component } from 'react';
import PropTypes from 'prop-types';

import debouncer from '../../../modules/debouncer';
import { dataUriToBlob, isBrowserUsingExifOrientation } from '../../../modules/ImageTools';
import ssPropTypes from '../../../modules/ssPropTypes';

import ImageEditor, { CROP_BORDERS } from './ImageEditor';

/**
 * Supplies `ImageEditor` with an image for rotating, zooming and cropping an image, and handles the
 * specifics of each of these operations. You can export the edited image with the `getImageDataURL`
 * or `getImageBlob` functions.
 *
 * @example
 * ```
 *  function MyImageEditor() {
 *    let editor;
 *
 *    function save() {
 *      // Get the image as a data URL
 *      const dataURL = editor.getImageDataURL();
 *
 *      // Or, get data as a raw binary blob.
 *      const dataBlob = editor.getImageBlob();
 *    }
 *
 *    function onLoad (e) {
 *      editor = e;
 *    }
 *
 *    return (
 *      <ImageEditor
 *        image={imageUrl}
 *        width={300}
 *        height={300}
 *        onEditorLoad={this.onLoad}
 *      />
 *    );
 *  }
 * ```
 */
class ImageEditorContainer extends Component {
  /**
   * Transforms a crop rectangle as would be provided in the `crop` property of the `onChange` event
   * into zoom and centerPoint properties to be provided to the react-avatar-editor.
   * @param {Object} param0 The crop parameters
   * @param {Number} param0.bottom The bottom crop amount
   * @param {Number} param0.top The top crop amount
   * @param {Number} param0.left The left crop amount
   * @param {Number} param0.right The right crop amount
   * @param {Number} param0.rotate The rotation angle, in degrees
   * @param {Object} param1 The dimensions of the original image
   * @param {Number} param1.width The original width of the image, in pixels
   * @param {Number} param1.height The original height of the image, in pixels
   * @returns {Object} An object with `zoom`, `centerPoint`, and `rotate` properties.
   */
  static transformCropToScaleAndPosition({
    rotate,
    bottom,
    top,
    left,
    right,
  }, {
    width,
    height,
  }) {
    const zoom = Math.round((width / (right - left)) * 100) / 100;
    const centerPoint = {
      x: ((right - left) / 2 + left) / width,
      y: ((bottom - top) / 2 + top) / height,
    };

    return {
      zoom,
      centerPoint,
      rotate,
    };
  }

  state = {
    zoom: 1,
    rotate: undefined,
    image: null,
    centerPoint: {
      x: 0.5,
      y: 0.5,
    },
  };

  imgInfo = null;
  editor = null;
  changeDebounce = debouncer();

  componentDidMount() {
    const { onEditorLoad } = this.props;
    if (onEditorLoad) {
      onEditorLoad(this);
    }
  }

  componentWillUnmount() {
    this.changeDebounce.stop();
  }

  /**
   * Export the image as a Blob object.
   * @return {Blob}
   */
  getImageBlob() {
    const dataURI = this.getImageDataURL().substr(5); // remove leading 'data:'
    return dataUriToBlob(dataURI);
  }

  /**
   * Get the image data as a jpeg data URL.
   * @return {String}
   */
  getImageDataURL() {
    const canvas = this.editor.getImage();
    return canvas.toDataURL('image/*', 1);
  }

  /**
   * Calculate the cropping window size
   * @param {boolean} adjust4Rotation - Swap height/width if the image is rotated sideways
   */
  getCropViewport(adjust4Rotation = true) {
    const { height, width, cropHeightMultiplier } = this.props;
    let editorHeight = 0;
    let editorWidth = 0;

    if (height > 0 && width > 0) {
      editorWidth = width - CROP_BORDERS * 2;
      editorHeight = editorWidth * cropHeightMultiplier;

      // Swap height/width if we're rotated
      if (this.isSideways() && adjust4Rotation) {
        const swapHeight = editorHeight;
        editorHeight = editorWidth;
        editorWidth = swapHeight;
      }
    }

    return {
      width: editorWidth,
      height: editorHeight,
    };
  }

  /**
   * Calculate the size and position of the cropping box
   */
  getCropCoords() {
    const { zoom, centerPoint } = this.state;

    // The image hasn't loaded yet
    if (!this.imgInfo || !this.imgInfo.resource) {
      return null;
    }

    const image = this.imgInfo.resource;
    const origWidth = image.width;
    const origHeight = image.height;
    const viewport = this.getCropViewport(false);

    // How much has the image been scaled to fit the viewer
    const imgScale = origWidth / this.imgInfo.width;

    // Adjust for scale & zoom
    const cropWidth = (viewport.width * imgScale) / zoom;
    const cropHeight = (viewport.height * imgScale) / zoom;

    let top = centerPoint.y * origHeight - cropHeight / 2;
    let left = centerPoint.x * origWidth - cropWidth / 2;
    if (top < 0) {
      top = 0;
    }
    if (left < 0) {
      left = 0;
    }

    let right = left + cropWidth;
    let bottom = top + cropHeight;
    if (right > origWidth) {
      right = origWidth;
    }
    if (bottom > origHeight) {
      bottom = origHeight;
    }

    return {
      top: Math.round(top),
      left: Math.round(left),
      right: Math.round(right),
      bottom: Math.round(bottom),
    };
  }

  /**
   * Fire the change handler
   */
  fireChange() {
    const { onChange } = this.props;
    const { zoom, rotate, centerPoint } = this.state;

    if (typeof onChange !== 'function') {
      return;
    }

    const box = this.getCropCoords();
    onChange({
      zoom,
      rotate,
      box,
      centerPoint,
      isBrowserUsingExifOrientation: isBrowserUsingExifOrientation(),
    });
  }

  /**
   * Has the image rotated sideways.
   */
  isSideways() {
    const { rotate } = this.state;
    return rotate === 90 || rotate === 270;
  }

  /**
   * The image has loaded in the editor
   */
  onImageLoaded = imgInfo => {
    // imgInfo.resource is an img tag
    this.imgInfo = imgInfo;

    const dimensions = {
      // https://www.w3schools.com/jsref/prop_img_naturalwidth.asp
      width: imgInfo.resource.naturalWidth,
      height: imgInfo.resource.naturalHeight,
    };
    const { initialCrop } = this.props;

    if (initialCrop) {
      const scaleAndPosition = ImageEditorContainer.transformCropToScaleAndPosition(
        initialCrop,
        dimensions,
      );

      this.setState(scaleAndPosition, () => this.fireChange());
    } else {
      this.setState(prevState => ({ rotate: prevState.rotate || 0 }), () => this.fireChange());
    }
  };

  /**
   * Change the rotation of the image.
   * @param {Number} deg - Rotate by this number of degrees
   */
  rotate = deg => {
    let { rotate } = this.state;
    rotate += deg;
    if (rotate === 360) {
      rotate = 0;
    }
    if (rotate < 0) {
      rotate += 360;
    }
    this.setState({ rotate }, () => this.fireChange());
  };

  /**
   * Rotate by 90 degrees clockwise
   */
  rotateCW = () => this.rotate(90);

  /**
   * The image zoom slider changed value.
   */
  onZoomChanged = value => this.setState({ zoom: Number(value) }, () => this.fireChange());

  /**
   * The image has been modified
   */
  onPositionChange = pos => {
    this.setState({ centerPoint: { ...pos } }, () => {
      this.changeDebounce(() => this.fireChange());
    });
  };

  render() {
    const { image, onEditorMounted, allowRotate } = this.props;
    const { zoom, rotate = 0, centerPoint } = this.state;
    const cropViewport = this.getCropViewport();
    const imageIsLoaded = image !== null;
    return !imageIsLoaded ? null : (
      <ImageEditor
        image={image}
        zoom={zoom}
        rotate={rotate}
        position={centerPoint}
        cropViewport={cropViewport}
        onEditorMounted={onEditorMounted}
        onPositionChange={this.onPositionChange}
        onImageLoaded={this.onImageLoaded}
        onZoomChanged={this.onZoomChanged}
        onRotateCW={this.rotateCW}
        allowRotate={allowRotate}
      />
    );
  }
}

export const ImageEditorContainerProps = {
  /** The image URL or file to edit. */
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File)]),

  /** The height of the image editor window. */
  height: ssPropTypes.numberLike.isRequired,

  /** The width of the image editor window. */
  width: ssPropTypes.numberLike.isRequired,

  /** Fired after the editor has been loaded. */
  onEditorLoad: PropTypes.func,

  /** Called when the editor component has mounted */
  onEditorMounted: PropTypes.func,

  /** Fired when the image has changed */
  onChange: PropTypes.func,

  /** The height ratio multiplier for cropping.
   *  Ratio - 1:<x> (width:height) */
  cropHeightMultiplier: PropTypes.number,

  /** Crop rectangle + rotation angle to apply to initial image */
  initialCrop: PropTypes.shape({
    top: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
    rotate: PropTypes.number,
  }),
  allowRotate: PropTypes.bool,
};

ImageEditorContainer.propTypes = ImageEditorContainerProps;

ImageEditorContainer.defaultProps = {
  onEditorLoad: null,
  onEditorMounted: null,
  onChange: null,
  cropHeightMultiplier: 1,
  initialCrop: null,
  image: null,
  allowRotate: false,
};

export default ImageEditorContainer;
